import { computed } from 'vue';
import { useFunctionGetter } from 'dashboard/composables/store';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

/**
 * Composable to extract translation state/content from contentAttributes.
 * @param {Ref|Reactive} contentAttributes - Ref or reactive object containing `translations` property
 * @param {Ref|number} [messageId] - The message's id, used to look up in-flight translate requests
 * @returns {Object} { hasTranslations, translationContent, translationLanguageName, isTranslating }
 */
export function useTranslations(contentAttributes, messageId) {
  const hasTranslations = computed(() => {
    if (!contentAttributes.value) return false;
    const { translations = {} } = contentAttributes.value;
    return Object.keys(translations || {}).length > 0;
  });

  const translationLanguageCode = computed(() => {
    if (!hasTranslations.value) return null;
    return Object.keys(contentAttributes.value.translations)[0];
  });

  const translationContent = computed(() => {
    if (!hasTranslations.value) return null;
    return contentAttributes.value.translations[translationLanguageCode.value];
  });

  const translationLanguageName = computed(() => {
    if (!translationLanguageCode.value) return '';
    return getLanguageName(translationLanguageCode.value);
  });

  const isTranslating = messageId
    ? useFunctionGetter('isMessageTranslating', messageId)
    : computed(() => false);

  return {
    hasTranslations,
    translationContent,
    translationLanguageName,
    isTranslating,
  };
}
